// AuthContext.js
import { API, TOKEN_KEY, getSECURE_API } from 'api';
import { getMe } from 'api/users';
import { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [state, setState] = useState({
    isLoggedIn: false,
    isAuthenticating: true,
    user: null
  });

  const [banners, setBanners] = useState([]);

  useEffect(() => {
    (async () => {
      const { data } = await API.get('/banners');
      setBanners(data.banners);
    })();
  }, []);

  const [transactions, setTransactions] = useState({
    isAuthenticating: true,
    data: [],
    walletTransactions: [],
    gameTransactions: [],
    withdrawTransactionsStats: [],
    depositTransactionsStats: [],
    redeemTransactionsStats: [],
    rechargeTransactionsStats: []
  });

  const [earnings, setEarnings] = useState({
    isAuthenticating: true,
    stats: null,
    data: []
  });

  const [stats, setStats] = useState({
    isAuthenticating: true,
    data: null
  });

  const getMyStats = async () => {
    try {
      const url = state.user === 'agent' ? 'users/agent-stats' : 'users/stats';
      const { data } = await getSECURE_API().get(url);
      setStats({
        isAuthenticating: false,
        data: data.stats
      });
    } catch (err) {
      console.error(err);
    } finally {
      setStats(st => ({
        ...st,
        isAuthenticating: false
      }));
    }
  };
  useEffect(() => {
    if (!state.isLoggedIn || !state.user)
      return setStats({
        isAuthenticating: false,
        data: null
      });
    getMyStats();
  }, [state.isLoggedIn, state.user]);

  const getMyTransactions = async () => {
    try {
      const { data } = await getSECURE_API().get('/funds/transactions');

      setTransactions({
        isAuthenticating: false,
        walletTransactions: data.walletTransactions,
        gameTransactions: data.gameTransactions,
        withdrawTransactionsStats: data.withdrawTransactionsStats,
        depositTransactionsStats: data.depositTransactionsStats,
        redeemTransactionsStats: data.redeemTransactionsStats,
        rechargeTransactionsStats: data.rechargeTransactionsStats
      });
    } catch (err) {
      console.error(err);
    } finally {
      setTransactions(st => ({
        ...st,
        isAuthenticating: false
      }));
    }
  };
  const getMyEarnings = async () => {
    try {
      const { data } = await getSECURE_API().get('/earnings');

      setEarnings({
        isAuthenticating: false,
        data: data.earnings,
        stats: data.earningsStats
      });
    } catch (err) {
      console.error(err);
    } finally {
      setEarnings(st => ({
        ...st,
        isAuthenticating: false
      }));
    }
  };

  const updateMe = data => {
    setState(st => ({
      ...st,
      user: {
        ...st.user,
        ...data
      }
    }));
  };

  const getMyProfile = async () => {
    try {
      const { data } = await getMe();

      setState({
        isAuthenticating: false,
        user: data.user,
        isLoggedIn: true
      });
      getMyTransactions();
      getMyEarnings();
    } catch (err) {
      console.error(err);
    } finally {
      setState(st => ({
        ...st,
        isAuthenticating: false
      }));
    }
  };

  useEffect(() => {
    getMyProfile();
  }, []);

  const navigate = useNavigate();

  const login = (_user, _token) => {
    // Logic to set the user as authenticated (e.g., after successful login)
    setState({
      isAuthenticating: false,
      isLoggedIn: true,
      user: _user
    });
    localStorage.setItem(TOKEN_KEY, _token);
    getMyTransactions();
    getMyEarnings();
  };

  const logout = () => {
    setState({
      isLoggedIn: false,
      user: null,
      isAuthenticating: false
    });
    setTransactions({
      isAuthenticating: false,
      stats: null,
      data: [],
      withdrawTransactionsStats: [],
      depositTransactionsStats: [],
      redeemTransactionsStats: [],
      rechargeTransactionsStats: []
    });

    setEarnings({
      isAuthenticating: false,
      stats: null,
      data: []
    });

    localStorage.removeItem(TOKEN_KEY);
  };

  return (
    <AuthContext.Provider
      displayName='Auth Context'
      value={{
        isLoggedIn: state.isLoggedIn,
        login,
        logout,
        isAuthenticating: state.isAuthenticating,
        user: state.user,
        transactions,
        stats,
        updateMe,
        setState,
        earnings,
        setEarnings,
        banners
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
